/* eslint-disable react-hooks/rules-of-hooks */
import { createStore, LayerRenderStatus } from "@react-pdf-viewer/core";
import * as React from "react";
import { Trigger } from "@react-pdf-viewer/highlight";

import { HIGHLIGHT_LAYER_ATTR, HIGHLIGHT_PAGE_ATTR } from "./constants";
import { HighlightAreaList } from "./highlight-area-list";
import { NO_SELECTION_STATE, SelectedState, SELECTING_STATE } from "./highlight-state";
import { Tracker } from "./tracker";

const TEXT_LAYER_END_SELECTOR = "rpv-highlight__selected-end";
const PAGE_LOADING_SELECTOR = '[data-testid^="core__page-layer-loading-"]';
const AREA_TOP_MARGIN = 10;
const SEEK_SCROLL_DURATION = 450;
const SEEK_TIMEOUT = 2000;

const pageLayerSelector = (pageIndex) => `[data-testid="core__page-layer-${pageIndex}"]`;

export const highlightPlugin = (props) => {
    const highlightPluginProps = Object.assign({}, { trigger: Trigger.TextSelection }, props);

    const store = React.useMemo(
        () =>
            createStore({
                highlightState: NO_SELECTION_STATE,
            }),
        [],
    );

    const pendingJump = React.useRef(null);

    const renderViewer = (props) => {
        const currentSlot = props.slot;
        if (highlightPluginProps.trigger !== Trigger.TextSelection) {
            return currentSlot;
        }

        if (currentSlot.subSlot && currentSlot.subSlot.children) {
            currentSlot.subSlot.children = (
                <>
                    <Tracker store={store} />
                    {currentSlot.subSlot.children}
                </>
            );
        }

        return currentSlot;
    };

    const handleMouseDown = (textLayerRender) => (e) => {
        const textLayer = textLayerRender.ele;
        const pageRect = textLayer.getBoundingClientRect();
        const highlightState = store.get("highlightState");
        if (highlightState instanceof SelectedState) {
            const mouseTop = e.clientY - pageRect.top;
            const mouseLeft = e.clientX - pageRect.left;

            // Check if the user clicks inside a highlighting area
            const userClickedInsideArea = highlightState.highlightAreas
                .filter((area) => area.pageIndex === textLayerRender.pageIndex)
                .find((area) => {
                    const t = (area.top * pageRect.height) / 100;
                    const l = (area.left * pageRect.width) / 100;
                    const h = (area.height * pageRect.height) / 100;
                    const w = (area.width * pageRect.width) / 100;
                    return (
                        t <= mouseTop && mouseTop <= t + h && l <= mouseLeft && mouseLeft <= l + w
                    );
                });
            if (userClickedInsideArea) {
                // Cancel the selection
                window.getSelection().removeAllRanges();
                store.update("highlightState", NO_SELECTION_STATE);
            } else {
                store.update("highlightState", SELECTING_STATE);
            }
        } else {
            store.update("highlightState", NO_SELECTION_STATE);
        }

        // Create an invisible element from the current position to the end of page
        // It prevents users from selecting the forward text
        const selectionTop = ((e.clientY - pageRect.top) * 100) / pageRect.height;
        const selectEnd = textLayer.querySelector(`.${TEXT_LAYER_END_SELECTOR}`);
        if (selectEnd && e.target !== textLayer) {
            selectEnd.style.top = `${Math.max(0, selectionTop)}%`;
        }
    };

    const handleMouseUp = (textLayerRender) => (e) => {
        const selectEnd = textLayerRender.ele.querySelector(`.${TEXT_LAYER_END_SELECTOR}`);
        if (selectEnd) {
            selectEnd.style.removeProperty("top");
        }
    };

    const handleContextMenu = () => (e) => {
        e.preventDefault();
    };

    const onTextLayerRender = (e) => {
        if (e.status === LayerRenderStatus.DidRender) {
            completePendingJump(e.pageIndex);
        }

        if (highlightPluginProps.trigger !== Trigger.TextSelection) {
            return;
        }

        const mouseDownHandler = handleMouseDown(e);
        const mouseUpHandler = handleMouseUp(e);
        const contextMenuHandler = handleContextMenu(e);
        const textEle = e.ele;

        if (e.status === LayerRenderStatus.PreRender) {
            textEle.removeEventListener("mousedown", mouseDownHandler);
            textEle.removeEventListener("mouseup", mouseUpHandler);
            textEle.removeEventListener("contextmenu", contextMenuHandler);

            const selectEndEle = textEle.querySelector(`.${TEXT_LAYER_END_SELECTOR}`);
            if (selectEndEle) {
                textEle.removeChild(selectEndEle);
            }
        } else if (e.status === LayerRenderStatus.DidRender) {
            textEle.addEventListener("mousedown", mouseDownHandler);
            textEle.addEventListener("mouseup", mouseUpHandler);
            textEle.addEventListener("contextmenu", contextMenuHandler);

            // Set some special attributes so we can query the text later
            textEle.setAttribute(HIGHLIGHT_LAYER_ATTR, "true");
            textEle
                .querySelectorAll(".rpv-core__text-layer-text")
                .forEach((span) => span.setAttribute(HIGHLIGHT_PAGE_ATTR, `${e.pageIndex}`));

            // Create an element that improves the text selection
            if (!textEle.querySelector(`.${TEXT_LAYER_END_SELECTOR}`)) {
                const selectEnd = document.createElement("div");
                selectEnd.classList.add(TEXT_LAYER_END_SELECTOR);
                selectEnd.classList.add("rpv-core__text-layer-text--not");
                textEle.appendChild(selectEnd);
            }
        }
    };

    const renderPageLayer = (renderPageProps) => (
        <HighlightAreaList
            pageIndex={renderPageProps.pageIndex}
            renderHighlightContent={highlightPluginProps.renderHighlightContent}
            renderHighlightTarget={highlightPluginProps.renderHighlightTarget}
            renderHighlights={highlightPluginProps.renderHighlights}
            store={store}
        />
    );

    const scrollToArea = (area) => {
        const jumpToDestination = store.get("jumpToDestination");

        if (jumpToDestination) {
            const bottomOffset = (_, viewportHeight) =>
                ((100 - area.top + AREA_TOP_MARGIN) * viewportHeight) / 100;
            const leftOffset = (viewportWidth, _) => ((100 - area.left) * viewportWidth) / 100;
            jumpToDestination(area.pageIndex, bottomOffset, leftOffset);
        }
    };

    const cancelPendingJump = () => {
        if (pendingJump.current) {
            clearTimeout(pendingJump.current.timer);
            pendingJump.current = null;
        }
    };

    const isPageMeasured = (pageIndex) => {
        const pagesContainer = store.get("getPagesContainer")?.();
        const pageLayer = pagesContainer?.querySelector(pageLayerSelector(pageIndex));

        return !!pageLayer && !pageLayer.querySelector(PAGE_LOADING_SELECTOR);
    };

    const jumpToHighlightArea = (area) => {
        cancelPendingJump();

        if (!area) {
            return;
        }

        const jumpToPage = store.get("jumpToPage");

        if (!jumpToPage || !Number.isInteger(area.pageIndex) || isPageMeasured(area.pageIndex)) {
            scrollToArea(area);
            return;
        }

        pendingJump.current = {
            area,
            seekStartedAt: performance.now(),
            timer: setTimeout(() => {
                pendingJump.current = null;
                scrollToArea(area);
            }, SEEK_TIMEOUT),
        };

        jumpToPage(area.pageIndex);
    };

    const completePendingJump = (pageIndex) => {
        const pending = pendingJump.current;

        if (!pending || pending.area.pageIndex !== pageIndex) {
            return;
        }

        clearTimeout(pending.timer);

        const remainingSeek = SEEK_SCROLL_DURATION - (performance.now() - pending.seekStartedAt);

        if (remainingSeek <= 0) {
            pendingJump.current = null;
            scrollToArea(pending.area);
            return;
        }

        pending.timer = setTimeout(() => {
            pendingJump.current = null;
            scrollToArea(pending.area);
        }, remainingSeek);
    };

    // Resets the selection of this viewer instance only
    const clearSelection = () => {
        store.update("highlightState", NO_SELECTION_STATE);
    };

    return {
        install: (pluginFunctions) => {
            store.update("jumpToDestination", pluginFunctions.jumpToDestination);
            store.update("jumpToPage", pluginFunctions.jumpToPage);
            store.update("getPagesContainer", pluginFunctions.getPagesContainer);
        },
        uninstall: cancelPendingJump,
        onViewerStateChange: (viewerState) => {
            store.update("rotation", viewerState.rotation);
            return viewerState;
        },
        onTextLayerRender,
        renderPageLayer,
        renderViewer,
        jumpToHighlightArea,
        clearSelection,
    };
};
